import { observer } from 'mobx-react';
import {
  FC, useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import dynamic from 'next/dynamic';
import { useTranslation } from 'next-i18next';
import classNames from 'classnames';

import { useCurrencyContext } from 'src/common/providers/CurrencyProvider';
import UniversalLink from 'src/common/components/UniversalLink';
import {
  INSTABOOK_FILTER_PARAM,
  PAGE_PARAM,
  PATHS,
  SEARCH_INSTABOOK_GTAG_EVENT,
} from 'src/common/constants';
import { trackEvent } from 'src/common/3rdParty/tracking';
import ScrollLock from 'src/common/components/ScrollLock';
import useAfterRenderCallback from 'src/common/hooks/useAfterRenderCallback';
import SortDropdown from 'src/search/components/SortDropdown';
import Typography from 'src/common/components/Typography';
import Button from 'src/common/components/ButtonDS22';
import PaginationAndListingsCount from 'src/search/components/PaginationAndListingsCount';
import useMobileNativeWebView from 'src/common/hooks/useMobileNativeWebView';
import MobileWebViewHeader from 'src/common/components/MobileWebViewHeader';
import JoyLoader from 'src/common/components/JoyLoader';
import { useMediaQueries } from 'src/common/providers/MediaQueryProvider';
import { SearchResults } from '../../../common/types/api/v4/boats/search';
import CommonMetaTags from '../../../common/components/MetaTags/CommonMetaTags';
import getClassNames from '../../../common/utils/getClassNames';
import { useInquiryIndicatorsContext } from '../../../common/providers/InquiryIndicatorsProvider';
import ListingCardList from '../../../common/components/ListingCardList';
import TopDestinations from '../../../common/components/TopDestinations';
import useGeolocation from '../../hooks/useGeolocation';
import HighlightedListingsCarousel from '../HighlightedListingsCarousel';
import LocationPrompt from '../LocationPrompt';
import LocationReviews from '../LocationReviews';
import LocationSummary from '../LocationSummary';
import MetaFields from '../MetaFields';
import NoResults from '../NoResults';
import Pagination from '../../../common/components/Pagination';
import ReturnLink from '../ReturnLink';
import SearchTitle from '../Title';
import useSearchStore from '../../store/useSearchStore';
import getLocationReviewTitleTranslation from '../../utils/getLocationReviewTitleTranslation';
import getLocationTitle from '../../utils/getLocationTitle';
import useScrollToElement from '../../utils/scrollToElement';
import { SeoData } from '../../../common/types/api/v4/rutter/v1/search/seo';
import MapToggleButton from '../MapToggleButton';
import ToggleMap from '../ToggleMap';
import Filters from '../Filters';

import styles from './SearchPageRoot.module.scss';

const InteractiveMap = dynamic(() => import('../InteractiveMap'), {
  loading: () => <div />,
  ssr: false,
});

const trackPagination = (isStructured: boolean) => (page: number) => {
  const label = isStructured ? 'Page has Carousel' : 'Page without Carousel';
  trackEvent('Pagination Clicked', {
    event_category: 'Search',
    event_label: label,
    value: page,
    non_interaction: false,
  });
};

type SearchPageRootProps = {
  searchResults: SearchResults;
  seo: SeoData;
  isNearbyLocationSearch: boolean;
  willPerformGeolocationSearch: boolean;
};

const SearchPageRoot = observer<FC<SearchPageRootProps>>(
  ({
    searchResults, seo, isNearbyLocationSearch, willPerformGeolocationSearch,
  }) => {
    const [searchPending, setSearchPending] = useState(willPerformGeolocationSearch);
    const { currency } = useCurrencyContext();
    const store = useSearchStore(searchResults, seo, currency?.code, isNearbyLocationSearch);
    const {
      showPrompt: showGeolocationPrompt,
      coords: geolocationCoordinates,
      error: geolocationError,
      dismissModal,
      handleLocationPromptAccept,
      handleLocationPromptDecline,
    } = useGeolocation(store.isNearbySearch.value);
    const { isMobileNativeWebView } = useMobileNativeWebView();

    const [mapOpen, setMapOpen] = useState(false);
    const activeBoatRef = useRef<HTMLDivElement>();
    const [t] = useTranslation('search-page');
    const [tBoatCategories] = useTranslation('boat-categories');
    const { ref } = useScrollToElement();
    const { desktop: isDesktop } = useMediaQueries();
    const inquiryIndicators = useInquiryIndicatorsContext();

    // Improve INP by allowing map/list toggle buttons to show click effect before
    // making an expensive page layout change
    const toggleMapOpen = useAfterRenderCallback(() => {
      const value = !mapOpen;
      if (!isDesktop && !isMobileNativeWebView) {
        if (window.screenY < 126 && value) {
          window.scrollTo(0, 126); // remnant of SearchAssisant, likely delete
        }
      }
      setMapOpen(value);
    });

    const closeMap = useCallback(() => setMapOpen(false), []);

    const hasResults = store.searchResults.results && store.searchResults.results.length > 0;

    useEffect(() => {
      requestAnimationFrame(() => {
        const activeBoatEl = activeBoatRef.current;
        if (activeBoatEl && isDesktop) {
          activeBoatEl.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
        }
      });
    }, [store.activeBoat, activeBoatRef, isDesktop]);

    const locationReviewTitleOptions = getLocationReviewTitleTranslation(
      store.location.value,
      store.category_slug.value,
      store.breadcrumbs,
      store.hasGeometry,
    );

    const locationTitle = store.location.value && getLocationTitle(store.breadcrumbs);

    useEffect(() => {
      if (geolocationCoordinates) {
        store.geolocation.onChange({
          clat: geolocationCoordinates.latitude,
          clon: geolocationCoordinates.longitude,
        });
        setSearchPending(false);
      }
    }, [geolocationCoordinates, store.geolocation]);

    useEffect(() => {
      if (geolocationError && searchPending) {
        setSearchPending(false);
      }
    }, [geolocationError, searchPending]);

    useEffect(() => {
      if (geolocationCoordinates || !geolocationError) {
        store.geolocationEnabled.onChange(true);
      }
      if (geolocationError || !geolocationCoordinates) {
        store.geolocationEnabled.onChange(false);
      }
    }, [geolocationCoordinates, geolocationError, store.geolocationEnabled]);

    const topCategoryHeader = useMemo(() => {
      if (store.topCategory) {
        const categorySlug = store.topCategory.slug.split('/')[0];
        const category = tBoatCategories(categorySlug, { count: 2 });
        return t('search-page--popular-category-heading', {
          location: locationTitle,
          category,
        });
      }
      return null;
    }, [locationTitle, store.topCategory, t, tBoatCategories]);

    const loading = store.isLoading || searchPending;

    const topCategoryCarousel = topCategoryHeader && !store.hasFiltersApplied;
    const instabookCarousel = !store.hasFiltersApplied;
    const hideInstabookCarousel = !store.searchResults.instabook_listings
      || (store.searchResults.instabook_listings
        && store.searchResults.instabook_listings.length < 4);

    const currentUrl = store.url;
    const instabookUrl = currentUrl ? `${currentUrl}&${INSTABOOK_FILTER_PARAM}` : '';

    return (
      <div className={getClassNames(styles, 'root', classNames({ isMobileNativeWebView }))}>
        {!isDesktop && mapOpen && <ScrollLock />}
        <div ref={ref} className={getClassNames(styles, 'gridWrapper', classNames({ mapOpen }))}>
          <LocationPrompt
            modalOpen={showGeolocationPrompt}
            closeModal={dismissModal}
            onAccept={handleLocationPromptAccept}
            onDecline={handleLocationPromptDecline}
          />
          {(store.geometry.value || store.location.value) && (
            <div className={getClassNames(styles, 'controls', classNames({ mapOpen, isMobileNativeWebView }))}>
              {/*
                MobileWebViewHeader returned here rather than in boat-rental/[[...slug]].tsx
                because MobileWebViewHeader needs to be within sticky controls
              */}
              <MobileWebViewHeader
                onBackOverride={mapOpen ? closeMap : undefined}
                catLocBreadcrumb={store.location.value}
              />
              <div className={styles.filters}>
                <Filters searchStore={store} />
                <div className={styles.mapViewFilter}>
                  <ToggleMap
                    value={mapOpen}
                    onToggle={toggleMapOpen}
                  />
                </div>
              </div>
            </div>
          )}
          <div className={getClassNames(styles, 'main', classNames({ mapOpen, noResults: !hasResults }))}>
            <CommonMetaTags
              noindex={store.noIndex}
              canonicalUrl={store.searchResults?.canonical_url}
            />
            <MetaFields
              seo={store.seo}
              searchResults={store.searchResults}
              noIndex={store.noIndex}
              breadcrumbs={store.breadcrumbs}
            />
            <div className={getClassNames(styles, 'header', classNames({ loading }))}>
              {store.category_slug.value !== '' && (
                <ReturnLink
                  location={store.location.value}
                  locationTitle={locationTitle}
                />
              )}
              <SearchTitle
                hasGeometry={store.hasGeometry}
                selectedCategories={store.titleCategories}
                h1FromRutterSEO={store.seo?.copy?.h1}
                isCustomSearch={store.isCustomSearch}
              />
              <div className={styles.summaryWithSort}>
                <div className={styles.summary}>
                  <LocationSummary
                    numberOfReviews={store.searchResults.aggregations?.total_boat_reviews}
                    rating={store.searchResults.aggregations?.boat_review_average}
                    breadcrumbs={store.breadcrumbs}
                    noResults={!hasResults}
                  />
                  {!topCategoryCarousel && (
                    <PaginationAndListingsCount
                      count={store.searchResults.count}
                      limit={store.searchResults.limit}
                      page={store.page.value}
                    />
                  )}
                </div>

                { !store.searchResults.nearby_fallback && !store.isNearbySearch.value
                && hasResults && (
                  <div className={styles.sort}>
                    <SortDropdown
                      value={store.sort.value}
                      onChange={store.sort.onChange}
                    />
                  </div>
                )}
              </div>
            </div>

            {topCategoryCarousel && (
              <HighlightedListingsCarousel
                listings={store.topCategory.results}
                parentBreadcrumbs={store.breadcrumbs}
                activeBoat={store.activeBoat}
                activeBoatRef={activeBoatRef}
                inquiryIndicators={inquiryIndicators}
                mapOpen={mapOpen}
                backgroundColor="neutral"
                variant="carouselItem"
                cardLocation="top category carousel"
                data-gulper-section="MostPopular"
              >
                <div className={styles.topCategoriesHeader}>
                  <h3 className={styles.highlightedListingsTitle}>
                    <UniversalLink href={`${PATHS.SEARCH}${store.topCategory.slug}/`}>
                      {topCategoryHeader}
                    </UniversalLink>
                  </h3>
                  <Button
                    variant="tertiary"
                    type="link"
                    size="small"
                    href={`${PATHS.SEARCH + store.topCategory.slug}/${PAGE_PARAM}`}
                    fitWidth
                  >
                    {t('search-page--view-all')}
                  </Button>
                </div>
              </HighlightedListingsCarousel>
            )}

            {instabookCarousel && (
              <div className={getClassNames(styles, 'instabookCarouselWrapper', classNames({ topCategoryCarousel }))}>
                <HighlightedListingsCarousel
                  listings={store.searchResults.instabook_listings}
                  parentBreadcrumbs={store.breadcrumbs}
                  activeBoat={store.activeBoat}
                  activeBoatRef={activeBoatRef}
                  inquiryIndicators={inquiryIndicators}
                  mapOpen={mapOpen}
                  backgroundColor="grey"
                  hideCarousel={hideInstabookCarousel}
                  gtagEvent={SEARCH_INSTABOOK_GTAG_EVENT}
                  instantBookingAvailable
                  data-gulper-section="BookNow"
                >
                  <div className={styles.instabookCarouselHeader}>
                    <div className={styles.instabookCarouselCopy}>
                      <h3 className={styles.highlightedListingsTitle}>
                        <UniversalLink href={instabookUrl}>
                          {t('instabook-listings--heading')}
                        </UniversalLink>
                      </h3>
                      <Typography
                        type="p"
                        size="body-m"
                        weight="regular"
                      >
                        {t('instabook-listings--description')}
                      </Typography>
                    </div>

                    <Button
                      variant="tertiary"
                      type="link"
                      size="small"
                      href={instabookUrl}
                      fitWidth
                    >
                      {t('search-page--view-all')}
                    </Button>
                  </div>
                </HighlightedListingsCarousel>
              </div>
            )}

            {hasResults && store.topCategory && (
              <div className={styles.listTitle}>
                <Typography
                  type="h3"
                  size="heading-3"
                  weight="semibold"
                >
                  {t('search-page--all-listings-heading', { location: locationTitle })}
                </Typography>
                {topCategoryCarousel && (
                  <PaginationAndListingsCount
                    count={store.searchResults.count}
                    limit={store.searchResults.limit}
                    page={store.page.value}
                  />
                )}
              </div>
            )}

            <div className={styles.mapToggleButton}>
              <MapToggleButton
                mapOpen={mapOpen}
                onToggle={toggleMapOpen}
                disabled={loading}
              />
            </div>
            <div className={getClassNames(styles, 'list', classNames({ loading }))}>
              {hasResults ? (
                <ListingCardList
                  listings={store.searchResults.results}
                  parentBreadcrumbs={store.breadcrumbs}
                  activeBoat={store.activeBoat}
                  activeBoatRef={activeBoatRef}
                  variant="primary"
                  classNameModifier={classNames({ mapOpen })}
                  inquiryIndicators={inquiryIndicators}
                  cardLocation="listing card"
                  data-gulper-section="Search"
                />
              ) : (
                <NoResults />
              )}
            </div>
            {store.searchResults.count > store.searchResults.limit && (
              <Pagination
                classNameModifier={classNames({
                  borderBottom:
                    store.searchResults.top_reviews.length === 0
                    && store.nearbyDestinations.length === 0,
                })}
                onChange={store.page.onChange}
                count={store.searchResults.count}
                limit={store.searchResults.limit}
                page={store.page.value}
                hrefBuilder={store.paginationHrefBuilder}
                trackPaginationClick={trackPagination(!!store.topCategory)}
              />
            )}
            <TopDestinations
              destinations={store.nearbyDestinations}
              classNameModifier="searchPage"
            />

            <LocationReviews
              title={t('location-reviews-title', locationReviewTitleOptions)}
              boats={store.searchResults.results}
              reviews={store.searchResults.top_reviews}
            />

            <div
              className={getClassNames(
                styles,
                'loading',
                classNames({ loading, mapOpen }),
              )}
            >
              <JoyLoader />
            </div>
          </div>
          {mapOpen && store.hasGeometry && (
            <div className={getClassNames(styles, 'aside', classNames({ isMobileNativeWebView }))}>
              <InteractiveMap
                initialBounds={store.geometry.fitBounds}
                onActiveBoatChange={store.onActiveBoatChange}
                onParameterChange={store.onMapMove}
                search={store.searchResults}
                count={store.searchResults.count}
                limit={store.searchResults.limit}
                page={store.page.value}
              />
            </div>
          )}
        </div>
      </div>
    );
  },
);

export default SearchPageRoot;
